/**
 * Erkennt, dass der Besucher K4ia ins Wort fällt — ohne die Echounterdrückung
 * des Browsers.
 *
 * Die ist aus (voiceClient.ts): Mit ihr legt iOS den ganzen Ton auf seinen
 * Telefonie-Pfad, und K4ias Stimme knackte. Ohne sie hört das Mikrofon K4ia
 * selbst, sobald sie spricht. Ein einfaches „Mikrofon laut → unterbrechen“
 * fiele sich deshalb ständig selbst ins Wort.
 *
 * Vor der Erkennung sitzt der eigene Echofilter (lib/echoFilter.ts): Er zieht
 * ihr Echo ab und schätzt, wie viel davon übrig ist. Ohne ihn (kein
 * AudioWorklet) vergleicht die Erkennung mit ihrem Pegel. Beides läuft hier
 * gleich: Verglichen werden zwei Pegel, der ihres Echos (`aus`) und der, der
 * im Mikrofon ankommt (`mic`). Das Echo ist ungefähr ein fester Anteil ihres Pegels — der Anteil
 * hängt von Gerät, Lautstärke und Raum ab und wird laufend mitgelernt, aber
 * nur aus Momenten, in denen niemand sonst spricht. Das erwartete Echo wird
 * herausgerechnet; bleibt eine Weile deutlich mehr übrig, spricht jemand.
 *
 * Drei Sicherungen gegen Fehlalarm:
 * - **Zeitgenau vergleichen**: Das Echo kommt verzögert an (Wiedergabe- und
 *   Aufnahmepuffer, 50–500 ms, je Gerät anders). Die Verzögerung wird aus der
 *   Korrelation beider Pegel gelernt; verglichen wird mit ihrem Pegel von
 *   damals, nicht von jetzt. Solange sie nicht gelernt ist, mit dem lautesten
 *   Wert der letzten halben Sekunde — vorsichtiger.
 * - **Anhalten**: Ein Husten oder ein Knall sind ein, zwei Blöcke — Sprache
 *   ist länger. Erst nach `halteBloecke` Blöcken mit Stimme (~250 ms) wird sie
 *   leiser, verstummen tut sie erst nach `mindestSekunden` (DazwischenAblauf).
 * - **Vorsichtig am Anfang**: Solange der Echoanteil noch nicht gelernt ist,
 *   gilt ein hoher Schätzwert — lieber einen Moment später unterbrechen als
 *   sich selbst.
 *
 * Reine Rechnung ohne Audio-API, damit sie sich prüfen lässt.
 */
export interface DazwischenOptionen {
  /**
   * Wie laut der Rest nach Abzug ihres Echos sein muss, als Anteil ihres
   * erwarteten Echos. Höher = unempfindlicher.
   */
  faktor: number
  /**
   * So viele Blöcke mit Stimme (je ~43 ms) innerhalb von `fenster` Blöcken,
   * bis es als Dazwischenreden gilt.
   */
  halteBloecke: number
  /**
   * Das Fenster dafür. Nicht „in Folge“: Eine Stimme, die gegen K4ia anredet,
   * liegt nur in manchen Blöcken über ihrem Echo — in den lauten Silben
   * von K4ia geht sie unter.
   */
  fenster: number
  /**
   * Darunter ist es nie Stimme (Effektivwert, 0…1) — solange die Stimme des
   * Besuchers noch nicht gemessen ist. Danach gilt ein Anteil davon, siehe
   * `stimmeGehoert`: Mikrofone liefern je Gerät sehr verschiedene Pegel, das
   * iPhone ohne die Echounterdrückung des Browsers etwa deutlich weniger als
   * ein Laptop.
   */
  minPegel: number
  /**
   * Welches Perzentil der gelernten Echoanteile als Echo gilt (Vorgabe 0,8).
   * Höher = vorsichtiger gegen einzelne laute Echoreste.
   */
  perzentil?: number
  /**
   * Die Verzögerung ihres Echos in Blöcken, wenn sie schon bekannt ist — hinter
   * dem Echofilter `0`: Sein geschätzter Rest gehört zum selben Block. Ohne
   * Angabe wird sie gelernt.
   */
  verzoegerung?: number
  /** Echoanteil, solange noch nichts gelernt ist (Vorgabe 1,2 — vorsichtig). */
  anfangsEcho?: number
  /** So lange muss der Besucher sprechen, bis K4ia verstummt (Vorgabe `MINDEST_SEKUNDEN`). */
  mindestSekunden?: number
}

/** Für das rohe Mikrofon, ohne eigenen Echofilter (Rückfall, etwa ohne AudioWorklet). */
export const STANDARD_OPTIONEN: DazwischenOptionen = {
  faktor: 0.6,
  halteBloecke: 6,
  fenster: 8,
  minPegel: 0.035,
}

/**
 * Für das Mikrofon hinter dem eigenen Echofilter (lib/echoFilter.ts). Dort ist
 * von K4ia wenig übrig, und verglichen wird mit dem Rest, den der Filter selbst
 * schätzt — also niedrigere Untergrenze, Fenster statt Folge. Abgestimmt in
 * einer Simulation mit iPhone-typischem Echo, bis 20 dB lauter als der
 * Besucher (docs/PROGRESS.md, 28.09.).
 */
export const OPTIONEN_HINTER_FILTER: DazwischenOptionen = {
  // 30.09.: Mit 2 und 4 wurde sie schon leiser, wenn man am Handy die
  // Lautstärke änderte — der Filter braucht einen Moment für den neuen Pegel.
  faktor: 3,
  halteBloecke: 5,
  fenster: 8,
  minPegel: 0.006,
  verzoegerung: 0,
  // Der Rest, den der Filter schätzt, ist schon „ihr Echo“ — Anteil 1.
  anfangsEcho: 1,
}

/** Die gemessene Stimme des Besuchers: Ab diesem Anteil davon zählt ein Block als Stimme. */
const ANTEIL_EIGENE_STIMME = 0.3
/** Nie unter diesen Pegel, auch bei sehr leisem Mikrofon (Effektivwert). */
const MIN_PEGEL_UNTERGRENZE = 0.002

/** Die selbstlernende Nachschärfung: höchstens so viel unempfindlicher. */
const MAX_NACHSCHAERFUNG = 3
/** … und durch Verdachte, die sich nicht bestätigt haben, höchstens so viel. */
const MAX_NACHSCHAERFUNG_LEISE = 2

/** Längste Echoverzögerung, die gesucht wird: 12 Blöcke ≈ 500 ms. */
const MAX_VERZOEGERUNG = 12
/** Darunter gilt der Lautsprecher als still (Satzpause). */
const STILL = 0.003
/** Echoanteil, solange noch nichts gelernt ist — bewusst hoch. */
const ANFANGS_ECHO = 1.2
/** So viele Messungen, bevor Echoanteil und Verzögerung gelten. */
const MINDEST_MESSUNGEN = 12
/** Gelernt wird aus den letzten ~3 s. */
const MAX_MESSUNGEN = 70
/** Nachhall: je Block klingt das Echo auf diesen Anteil ab, so viele Blöcke weit. */
const NACHHALL = 0.7
const NACHHALL_BLOECKE = 6
/** Wie schnell die Korrelation je Verzögerung vergisst (je Block). */
const VERGESSEN = 0.97
/** Ab dieser Korrelation gilt eine Verzögerung als gefunden. */
const KORRELATION_MIN = 0.5
/**
 * Ohne gefundene Verzögerung (kaum oder gar kein Echo, etwa mit Kopfhörern)
 * wird nur aus Blöcken gelernt, in denen das Mikrofon deutlich leiser ist als
 * sie — dann ist es sicher kein Besucher, der gerade spricht.
 */
const OHNE_ECHO_HOECHSTENS = 0.35

export class DazwischenErkennung {
  /** Lautsprecher-Verlauf, neuester Wert zuletzt. */
  private ausVerlauf: number[] = []
  private anteile: number[] = []
  /**
   * Womit die Anteile gemessen wurden: gegen ihren Pegel bei der gelernten
   * Verzögerung oder — solange es keine gibt — gegen ihren lautesten der
   * letzten halben Sekunde. Gelernt und verglichen wird immer mit demselben;
   * wechselt es, fängt das Lernen neu an.
   */
  private anteileModus: 'verzoegert' | 'hoechstwert' = 'hoechstwert'
  /**
   * Laufende Summen je Verzögerung für die Korrelation (nach Pearson, also um
   * den Mittelwert bereinigt: zwei Pegel sind immer positiv und sähen sonst
   * auch ohne jeden Zusammenhang ähnlich aus).
   */
  private sx = new Array<number>(MAX_VERZOEGERUNG).fill(0)
  private sxx = new Array<number>(MAX_VERZOEGERUNG).fill(0)
  private sxy = new Array<number>(MAX_VERZOEGERUNG).fill(0)
  private sy = 0
  private syy = 0
  private gewicht = 0
  private messungen = 0
  /** Ausgewertete Blöcke, in denen gelernt werden durfte. */
  private gesehen = 0
  private rauschen = 0.004
  /** Die letzten `fenster` Blöcke: Stimme ja/nein. */
  private verlauf: boolean[] = []
  /** Typischer Pegel der Stimme des Besuchers, `0`: noch nicht gemessen. */
  private stimmPegel = 0
  private stimmProben: number[] = []
  /**
   * Multipliziert den Faktor: steigt nach einem Fehlalarm (sie hat sich
   * unterbrochen, und dann sagte niemand etwas), sinkt langsam nach jedem
   * echten Dazwischenreden. So stellt sich die Erkennung auf einen lauten,
   * halligen Raum ein, den keine feste Zahl vorhersehen kann.
   */
  private nachschaerfung = 1
  /** Letzter Stand — für die Anzeige mit `?debug`. */
  letzte = {
    mic: 0,
    aus: 0,
    echo: ANFANGS_ECHO,
    schwelle: 0,
    verzoegerung: -1,
    nachschaerfung: 1,
    stimmPegel: 0,
    /** Stimmhaftigkeit des Blocks (0…1), `null` ohne Messung. */
    stimmhaft: null as number | null,
  }

  constructor(private readonly opt: DazwischenOptionen = STANDARD_OPTIONEN) {}

  /** Neuer Beitrag von K4ia: Zähler auf null, Gelerntes (Echo, Verzögerung) behalten. */
  neuerBeitrag(): void {
    this.verlauf = []
  }

  /**
   * Ein Block, während der Besucher spricht und K4ia still ist (Mikrofon
   * offen). Daraus lernt die Erkennung, wie laut er in diesem Mikrofon klingt —
   * die Schwelle richtet sich danach, nicht nach einer festen Zahl.
   */
  stimmeGehoert(rms: number): void {
    this.rauschFolgen(rms)
    // Nur, was deutlich über dem Raum liegt: Atempausen zählen nicht.
    if (rms < Math.max(this.rauschen * 3, MIN_PEGEL_UNTERGRENZE * 2)) return
    this.stimmProben.push(rms)
    if (this.stimmProben.length > 200) this.stimmProben.shift()
    if (this.stimmProben.length >= 10) {
      const sortiert = [...this.stimmProben].sort((a, b) => a - b)
      this.stimmPegel = sortiert[Math.floor(sortiert.length / 2)]!
    }
  }

  /**
   * Das Rauschen des Raums als Untergrenze der gemessenen Pegel: schnell
   * hinunter, langsam hinauf (~1 dB/s). Ein gleitender Mittelwert schaukelte
   * sich an leiser Stimme hoch — bis keine Stimme mehr über „4× Rauschen“ lag.
   */
  private rauschFolgen(rms: number): void {
    this.rauschen = rms < this.rauschen ? this.rauschen * 0.7 + rms * 0.3 : this.rauschen * 1.005
  }

  /** Die Untergrenze für Stimme: aus der gemessenen Stimme, sonst die Vorgabe. */
  private minPegel(): number {
    if (this.stimmPegel <= 0) return this.opt.minPegel
    return Math.min(
      this.opt.minPegel,
      Math.max(MIN_PEGEL_UNTERGRENZE, this.stimmPegel * ANTEIL_EIGENE_STIMME),
    )
  }

  /** Unterbrochen, aber danach kam nichts: Es war ihr eigenes Echo. */
  fehlalarm(): void {
    this.nachschaerfung = Math.min(MAX_NACHSCHAERFUNG, this.nachschaerfung * 1.5)
  }

  /** Unterbrochen, und dann kam ein Satz: Es war richtig. */
  bestaetigt(): void {
    this.nachschaerfung = Math.max(1, this.nachschaerfung * 0.9)
  }

  /** Der gelernte Anteil ihres Pegels, der als Echo im Mikrofon landet. */
  echoAnteil(): number {
    if (this.anteile.length < MINDEST_MESSUNGEN) return this.opt.anfangsEcho ?? ANFANGS_ECHO
    // Das 80. Perzentil: das typische Echo mit Luft nach oben — einzelne
    // laute Silben sollen nicht jedes Mal knapp an die Schwelle kommen.
    const sortiert = [...this.anteile].sort((a, b) => a - b)
    const p = this.opt.perzentil ?? 0.8
    return sortiert[Math.min(sortiert.length - 1, Math.floor(sortiert.length * p))]!
  }

  /**
   * Die Verzögerung (in Blöcken), mit der ihr Pegel im Mikrofon wieder
   * auftaucht — die mit der höchsten Korrelation. `-1`: noch nicht gelernt.
   */
  verzoegerung(): number {
    if (this.opt.verzoegerung !== undefined) return this.opt.verzoegerung
    if (this.messungen < MINDEST_MESSUNGEN || this.gewicht <= 0) return -1
    const w = this.gewicht
    const my = this.sy / w
    const vy = this.syy / w - my * my
    let beste = -1
    let besteKorrelation = KORRELATION_MIN
    for (let d = 0; d < MAX_VERZOEGERUNG; d++) {
      const mx = this.sx[d]! / w
      const vx = this.sxx[d]! / w - mx * mx
      const kov = this.sxy[d]! / w - mx * my
      const nenner = Math.sqrt(vx * vy)
      const k = nenner > 1e-12 ? kov / nenner : 0
      if (k > besteKorrelation) {
        besteKorrelation = k
        beste = d
      }
    }
    return beste
  }

  /**
   * Ihr Pegel zu dem Zeitpunkt, dessen Echo jetzt im Mikrofon ankommt — bei
   * der gelernten Verzögerung, mit einem Block Luft zu beiden Seiten und mit
   * Nachhall: Ein Raum klingt nach, das Echo einer lauten Silbe steht noch im
   * Mikrofon, wenn die nächste leiser ist. Ohne das sähe jede Silbenpause wie
   * fremde Stimme aus, und gelernt würde ein viel zu lautes Echo.
   */
  private bezug(): number {
    const n = this.ausVerlauf.length
    const d = this.verzoegerung()
    // Noch nicht gelernt: mit dem lautesten Wert vergleichen — vorsichtig.
    if (d < 0) return Math.max(...this.ausVerlauf)
    const bei = (k: number) => this.ausVerlauf[n - 1 - k] ?? 0
    let huelle = 0
    for (let k = Math.min(d + NACHHALL_BLOECKE, MAX_VERZOEGERUNG); k >= d - 1; k--) {
      huelle = Math.max(bei(k), huelle * NACHHALL)
    }
    return huelle
  }

  /**
   * Ein Block: Mikrofon und Lautsprecher als Effektivwert (0…1).
   * `true` genau in dem Block, in dem das Dazwischenreden feststeht.
   */
  update(mic: number, aus: number, stimmhaft?: number): boolean {
    const stimme = this.block(mic, aus, true, 1, stimmhaft)
    this.verlauf.push(stimme)
    if (this.verlauf.length > this.opt.fenster) this.verlauf.shift()
    const mitStimme = this.verlauf.reduce((n, v) => n + (v ? 1 : 0), 0)
    if (mitStimme >= this.opt.halteBloecke) {
      this.verlauf = []
      return true
    }
    return false
  }

  /**
   * Ein Block, nur die Frage „Stimme ja/nein“ — ohne zu lernen. Für die
   * Prüfung nach einem Verdacht (DazwischenAblauf): Da ist sehr wahrscheinlich
   * jemand im Mikrofon, und das soll nicht als ihr Echo gelernt werden.
   */
  istStimme(mic: number, aus: number, stimmhaft?: number, strenge = PRUEF_STRENGE): boolean {
    return this.block(mic, aus, false, strenge, stimmhaft)
  }

  /**
   * Ein Verdacht, der sich nicht bestätigt hat: etwas unempfindlicher — aber
   * höchstens bis ×2. Er kostet nur eine halbe Sekunde leisere Stimme; wer
   * danach wirklich dazwischenredet, soll nicht gegen eine Mauer reden.
   */
  leiserFehlalarm(): void {
    this.nachschaerfung = Math.max(
      this.nachschaerfung,
      Math.min(MAX_NACHSCHAERFUNG_LEISE, this.nachschaerfung * 1.2),
    )
  }

  /**
   * Hat sie genug Blöcke gesehen, um ihr Echo einzuschätzen? Gezählt wird
   * jeder ausgewertete Block — auch ohne Echo (Kopfhörer), sonst würde die
   * Erkennung dort nie scharf.
   */
  get eingelernt(): boolean {
    return this.gesehen >= MINDEST_MESSUNGEN
  }

  private block(
    mic: number,
    aus: number,
    lernen: boolean,
    strenge = 1,
    stimmhaft?: number,
  ): boolean {
    if (lernen) this.gesehen++
    this.ausVerlauf.push(aus)
    if (this.ausVerlauf.length > MAX_VERZOEGERUNG + 1) this.ausVerlauf.shift()
    const ref = this.bezug()
    const echo = this.echoAnteil()

    // Das erwartete Echo aus dem Mikrofonsignal herausrechnen (Leistungen
    // addieren sich): Was übrig bleibt, kann ihre Stimme nicht erklären.
    const erwartet = ref < STILL ? 0 : echo * ref
    const rest = Math.sqrt(Math.max(0, mic * mic - erwartet * erwartet))
    const schwelle = Math.max(
      this.opt.faktor * this.nachschaerfung * erwartet * strenge,
      this.rauschen * 4,
      this.minPegel(),
    )
    // Laut genug **und** wie eine Stimme: Ein Geräusch kann lauter sein als
    // ihr Echo, schwingt aber nicht. Ohne Messung (Rückfall) nur der Pegel.
    const klingtNachStimme = stimmhaft === undefined || stimmhaft >= STIMMHAFT_AB
    const stimme = rest > schwelle && klingtNachStimme
    this.letzte = {
      stimmhaft: stimmhaft ?? null,
      mic,
      aus: ref,
      echo,
      schwelle,
      verzoegerung: this.verzoegerung(),
      nachschaerfung: this.nachschaerfung,
      stimmPegel: this.stimmPegel,
    }

    // Gelernt wird nur, was sicher Echo ist — nicht schon, wenn es knapp unter
    // der Schwelle liegt. Sonst lernte die Erkennung eine beginnende Stimme als
    // Echo mit und würde mit jedem Wort unempfindlicher.
    const sicherEcho = ref < STILL ? mic < schwelle : mic < 1.2 * erwartet
    if (lernen && !stimme && sicherEcho) {
      const laut = Math.max(...this.ausVerlauf)
      if (laut < STILL) {
        // Satzpause: Das ist das Rauschen des Raums.
        this.rauschFolgen(mic)
      } else {
        this.lernen(mic)
        // Mit gefundener Verzögerung: gegen ihren Pegel von damals. Ohne —
        // kaum Echo, etwa mit Kopfhörern — nur aus Blöcken, in denen das
        // Mikrofon klar leiser ist als sie. Die ersten Blöcke eines Beitrags,
        // bevor ihr Echo ankommt, zählen so nicht als „winziges Echo“, und wer
        // früh dazwischenredet, wird nicht als Echo gelernt.
        const modus = this.verzoegerung() >= 0 ? 'verzoegert' : 'hoechstwert'
        if (modus !== this.anteileModus) {
          this.anteile = []
          this.anteileModus = modus
        }
        const lernbar = ref >= STILL && (modus === 'verzoegert' || mic < OHNE_ECHO_HOECHSTENS * ref)
        if (lernbar) {
          this.anteile.push(mic / ref)
          if (this.anteile.length > MAX_MESSUNGEN) this.anteile.shift()
        }
      }
    }
    return stimme
  }

  /** Korrelation je Verzögerung fortschreiben — nur aus Blöcken ohne fremde Stimme. */
  private lernen(mic: number): void {
    const n = this.ausVerlauf.length
    this.gewicht = this.gewicht * VERGESSEN + 1
    this.sy = this.sy * VERGESSEN + mic
    this.syy = this.syy * VERGESSEN + mic * mic
    for (let d = 0; d < MAX_VERZOEGERUNG; d++) {
      const a = this.ausVerlauf[n - 1 - d] ?? 0
      this.sx[d] = this.sx[d]! * VERGESSEN + a
      this.sxx[d] = this.sxx[d]! * VERGESSEN + a * a
      this.sxy[d] = this.sxy[d]! * VERGESSEN + mic * a
    }
    this.messungen++
  }
}

/**
 * Was ein Block auslöst:
 * - `verdacht`: Da könnte jemand sprechen — still prüfen, K4ia redet in voller
 *   Lautstärke weiter. (Bis 30.09. wurde sie schon hier leiser — bei jedem
 *   Geräusch.)
 * - `deutlich`: Seit ~½ s eindeutig Stimme — jetzt wird sie leiser
 *   (PcmAudioPlayer.ducken) und zeigt, dass sie zuhört.
 * - `bestaetigt`: Da spricht jemand — K4ia verstummt, das Mikrofon geht auf.
 * - `entwarnung`: Es war nichts — K4ia wird wieder laut und spricht weiter.
 * - `kurz`: Ein kurzer Ansatz, schon wieder vorbei — vielleicht ein Kommando
 *   („Stopp“). Der Aufrufer lässt ihn prüfen und meldet das Ergebnis mit
 *   `stichwort()`; bis dahin bleibt K4ia leiser.
 */
export type DazwischenSchritt =
  'nichts' | 'verdacht' | 'deutlich' | 'bestaetigt' | 'entwarnung' | 'kurz'

/** Ein Block des Mikrofon-Worklets: 2048 Samples, bei 48 kHz rund 43 ms. */
export const BLOCK_SEKUNDEN = 2048 / 48000
/**
 * So lange muss der Besucher sprechen, bis K4ia verstummt (Vorgabe). Kürzer
 * hielt sie für jedes Husten, Lachen oder Räuspern inne (30.09.) — ein Satz,
 * der sie wirklich unterbrechen soll, dauert länger als eine Sekunde.
 */
export const MINDEST_SEKUNDEN = 1
/** Von der Prüfung muss mindestens so viel Stimme sein — Sprache hat Lücken zwischen Wörtern. */
const STIMM_ANTEIL = 0.5
/**
 * Am Ende der Mindestdauer muss er noch sprechen: in so vielen der letzten
 * Blöcke Stimme … Ein Husten ist dann längst vorbei.
 */
const ENDE_BLOECKE = 6
const ENDE_STIMME = 3
/** So lange Stille (~0,4 s) in der Prüfung heißt Entwarnung — länger als die Pause zwischen zwei Wörtern. */
export const STILLE_ENTWARNUNG = 10
/** Spätestens so viele Blöcke nach der Mindestdauer ist die Prüfung vorbei. */
const NACHFRIST_BLOECKE = 20
/**
 * Ein kurzer Ansatz: so viele Blöcke Stimme (~130 ms, ein „Stopp“ dauert
 * 300–500 ms), dann so viel Stille (~200 ms) — dann wird geprüft, ob es ein
 * Stichwort war, statt auf die Sekunde zu warten, die nie kommt.
 */
const KURZ_STIMME = 3
const KURZ_STILLE = 5
/** So viele Blöcke Stimme (~0,4 s), bis sie leiser wird — vorher hört man nichts von der Prüfung. */
export const DEUTLICH_BLOECKE = 10
/** So lange (~1,7 s) wartet sie höchstens auf das Ergebnis der Prüfung. */
export const STICHWORT_WARTEN_BLOECKE = 40

/**
 * In der Prüfung zählt ihr Echo doppelt: K4ia ist dann rund 16 dB leiser, eine
 * echte Stimme liegt weit darüber — ein Rest ihres Echos nicht.
 */
const PRUEF_STRENGE = 2

/**
 * Dazwischenreden in zwei Stufen — wie ein Mensch, der merkt, dass der andere
 * ansetzt: erst leiser werden, dann hinhören, dann aufhören oder weiterreden.
 *
 * Der Gewinn ist doppelt. Ein Fehlalarm kostet nur einen Moment leisere
 * Stimme statt eines abgebrochenen Satzes — die erste Stufe darf also
 * empfindlich sein. Und die Prüfung ist leicht: Ist K4ia leise, ist auch ihr
 * Echo leise (am iPhone 15 dB weniger), und wer wirklich spricht, steht
 * deutlich darüber.
 */
export class DazwischenAblauf {
  readonly erkennung: DazwischenErkennung
  /** Blöcke Prüfung, bis K4ia verstummen darf. */
  readonly mindestBloecke: number
  private pruefen = false
  private gezaehlt = 0
  private mitStimme = 0
  private stilleInFolge = 0
  private letzte: boolean[] = []
  /** Der kurze Ansatz wird gerade geprüft (seit so vielen Blöcken, sonst -1). */
  private wartetSeit = -1
  private kurzGemeldet = false
  private deutlichGemeldet = false

  constructor(opt: DazwischenOptionen = STANDARD_OPTIONEN) {
    this.erkennung = new DazwischenErkennung(opt)
    this.mindestBloecke = Math.ceil((opt.mindestSekunden ?? MINDEST_SEKUNDEN) / BLOCK_SEKUNDEN)
  }

  /** Gerade in der Prüfung (ab `deutlich` ist K4ia leiser)? */
  get imVerdacht(): boolean {
    return this.pruefen
  }

  /** Neuer Beitrag von K4ia oder sie hat ausgeredet: Prüfung abbrechen. */
  neuerBeitrag(): void {
    this.pruefen = false
    this.erkennung.neuerBeitrag()
  }

  /** `stimmhaft`: siehe `stimmhaftigkeit()` — ohne Messung zählt nur der Pegel. */
  update(mic: number, aus: number, stimmhaft?: number): DazwischenSchritt {
    if (!this.pruefen) {
      // Erst ihr Echo kennenlernen (~½ s), dann urteilen: Mit dem vorsichtigen
      // Schätzwert vom Anfang hielt die Erkennung ihren ersten Satz sonst
      // mitunter für einen Besucher.
      const treffer = this.erkennung.update(mic, aus, stimmhaft)
      if (!treffer || !this.erkennung.eingelernt) return 'nichts'
      this.pruefen = true
      this.gezaehlt = 0
      this.mitStimme = 0
      this.stilleInFolge = 0
      this.letzte = []
      this.wartetSeit = -1
      this.kurzGemeldet = false
      this.deutlichGemeldet = false
      return 'verdacht'
    }
    this.gezaehlt++
    // Erst wenn sie leiser ist (`deutlich`), ist auch ihr Echo leiser — dann
    // darf die Prüfung strenger sein. Vorher spricht sie in voller Lautstärke.
    const stimme = this.erkennung.istStimme(
      mic,
      aus,
      stimmhaft,
      this.deutlichGemeldet ? PRUEF_STRENGE : 1,
    )
    if (stimme) this.mitStimme++
    this.stilleInFolge = stimme ? 0 : this.stilleInFolge + 1
    this.letzte.push(stimme)
    if (this.letzte.length > ENDE_BLOECKE) this.letzte.shift()

    if (this.wartetSeit >= 0) {
      // Die Prüfung läuft: Stille entwarnt nicht, das Ergebnis entscheidet —
      // oder er spricht weiter und kommt so über die Sekunde.
      if (this.gezaehlt - this.wartetSeit >= STICHWORT_WARTEN_BLOECKE) return this.entwarnen()
    } else if (
      !this.kurzGemeldet &&
      this.mitStimme >= KURZ_STIMME &&
      this.stilleInFolge >= KURZ_STILLE
    ) {
      // Kurz gesprochen, schon wieder still: ein Kommando oder ein Husten.
      this.kurzGemeldet = true
      this.wartetSeit = this.gezaehlt
      return 'kurz'
    } else if (this.stilleInFolge >= STILLE_ENTWARNUNG) {
      // Husten, Räuspern, ein Knall: kurz laut, dann still — K4ia redet weiter.
      return this.entwarnen()
    }
    if (this.gezaehlt >= this.mindestBloecke) {
      const nochDabei = this.letzte.filter(Boolean).length >= ENDE_STIMME
      if (nochDabei && this.mitStimme >= STIMM_ANTEIL * this.gezaehlt) {
        this.pruefen = false
        this.erkennung.neuerBeitrag()
        return 'bestaetigt'
      }
      if (this.gezaehlt >= this.mindestBloecke + NACHFRIST_BLOECKE) return this.entwarnen()
    }
    if (!this.deutlichGemeldet && this.mitStimme >= DEUTLICH_BLOECKE) {
      this.deutlichGemeldet = true
      return 'deutlich'
    }
    return 'nichts'
  }

  /**
   * Das Ergebnis der Prüfung nach `kurz`: ein Stichwort → sie verstummt,
   * sonst redet sie weiter. Kommt es zu spät (Prüfung schon vorbei), `nichts`.
   */
  stichwort(treffer: boolean): DazwischenSchritt {
    if (!this.pruefen || this.wartetSeit < 0) return 'nichts'
    if (!treffer) return this.entwarnen()
    this.pruefen = false
    this.erkennung.neuerBeitrag()
    return 'bestaetigt'
  }

  private entwarnen(): DazwischenSchritt {
    this.pruefen = false
    this.erkennung.leiserFehlalarm()
    this.erkennung.neuerBeitrag()
    return 'entwarnung'
  }
}

/**
 * Wie stimmhaft ein Stück Aufnahme ist (0…1): die höchste normierte
 * Autokorrelation im Bereich einer Sprechstimme (80–400 Hz Grundfrequenz).
 *
 * Eine Stimme schwingt periodisch — Vokale, „m“, „n“, „l“. Rascheln, Klopfen,
 * Husten, Atmen, ein Klick beim Lautstärkeknopf sind Rauschen und Impulse
 * ohne Periode. Am Pegel allein ist das nicht zu unterscheiden; daran schon
 * (30.09.: „bei jedem kleinen Geräusch wird sie leise“).
 *
 * `rate` ist die Abtastrate des Stücks (hinter dem Echofilter 16 kHz).
 */
export function stimmhaftigkeit(samples: Float32Array, rate = 16000): number {
  const n = samples.length
  const minLag = Math.floor(rate / 400)
  const maxLag = Math.min(Math.ceil(rate / 80), Math.floor(n / 2))
  if (maxLag <= minLag) return 0
  let mittel = 0
  for (let i = 0; i < n; i++) mittel += samples[i]!
  mittel /= n
  const x = new Float32Array(n)
  for (let i = 0; i < n; i++) x[i] = samples[i]! - mittel
  let beste = 0
  for (let lag = minLag; lag <= maxLag; lag++) {
    let kreuz = 0
    let a = 0
    let b = 0
    for (let i = 0; i + lag < n; i++) {
      const u = x[i]!
      const v = x[i + lag]!
      kreuz += u * v
      a += u * u
      b += v * v
    }
    const nenner = Math.sqrt(a * b)
    if (nenner > 1e-12) beste = Math.max(beste, kreuz / nenner)
  }
  return beste
}

/**
 * Ab hier gilt ein Block als Stimme. Gesprochene Vokale liegen meist über
 * 0,6, Rauschen und Geräusche unter 0,3; dazwischen liegen Konsonanten —
 * die zählen nicht, die Vokale drumherum reichen.
 */
export const STIMMHAFT_AB = 0.45

/** Effektivwert eines Blocks (0…1), roh — ohne die Verstärkung der Pegelanzeige. */
export function effektivwert(samples: Float32Array): number {
  let summe = 0
  for (let i = 0; i < samples.length; i++) summe += samples[i]! * samples[i]!
  return Math.sqrt(summe / (samples.length || 1))
}

/**
 * Feinabstimmung am Gerät, nur für Tests: `?dazwischen=aus` schaltet es ab,
 * `?dazwischen=0.9` setzt den Faktor (höher = unempfindlicher),
 * `?dazwischenSek=1.5` die Mindestdauer in Sekunden.
 */
export function dazwischenOptionenAusAdresse(
  basis: DazwischenOptionen = STANDARD_OPTIONEN,
): DazwischenOptionen | null {
  try {
    const wert = new URLSearchParams(window.location.search).get('dazwischen')
    if (wert === 'aus') return null
    const faktor = wert ? Number(wert) : NaN
    const sek = Number(new URLSearchParams(window.location.search).get('dazwischenSek') ?? NaN)
    return {
      ...basis,
      ...(Number.isFinite(faktor) && faktor > 0 ? { faktor } : {}),
      ...(Number.isFinite(sek) && sek >= 0.3 && sek <= 5 ? { mindestSekunden: sek } : {}),
    }
  } catch {
    return basis
  }
}
